import { Stack, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useGetChargeByInvoiceQuery } from "src/api/services/chargesApi";
import { PatientOrder } from "src/api/types";
import { usePatientSubscriptions } from "src/context/PatientSubscriptionsProvider";
import { useIsMobile } from "src/hooks/useIsMobile";
import InjectionGuideCard from "src/pages/PatientDashboard/Care/components/InjectionGuideCard";
import MedicationCard from "src/pages/PatientDashboard/Care/components/MedicationCard";
import MedRemindersCard from "src/pages/PatientDashboard/Care/components/MedRemindersCard";
import MessageCard from "src/pages/PatientDashboard/Care/components/MessageCard";
import TreatmentPlanCard from "src/pages/PatientDashboard/Care/components/TreatmentPlanCard";
import ManageSubscriptionCard from "../ManageSubscriptionCard";
import ReactivateMembershipActionItem from "../ReactivateMembershipActionItem";
import RecommendedDose from "../RecommendedDose";
import { useGetPrescriptionDetailsQuery } from "src/api/services/prescriptionsApi";

interface Props {
  order: PatientOrder;
  page: string;
}

const TreatmentOverviewSection = ({ order, page }: Props) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const subscription = usePatientSubscriptions(
    (subs) =>
      subs.find(
        (sub) =>
          sub.product.slug === order.product.slug && sub.status === "paid",
      ) ?? null,
  );

  const { data: details, isLoading } = useGetPrescriptionDetailsQuery(
    order.prescription_id,
  );

  const { data: charge } = useGetChargeByInvoiceQuery(
    subscription == null && order.invoice_id != null
      ? order.invoice_id
      : skipToken,
  );

  const isGlp1 = useMemo(
    () => !!order.product.is_glp1,
    [order?.product.is_glp1],
  );

  const numberOfMonths = useMemo(() => {
    if (subscription == null && charge != null) {
      return charge.price.number_of_months;
    }

    return subscription?.price.number_of_months ?? 0;
  }, [charge, subscription]);

  const category = useMemo(() => {
    return order.product_category.name ?? "";
  }, [order.product_category.name]);

  if (isLoading) {
    return null;
  }

  return (
    <Stack gap={2}>
      <ReactivateMembershipActionItem />
      {details == null || details.medications.length === 0 ? (
        <Typography>{t(`${page}.message`)}</Typography>
      ) : (
        <>
          <MedicationCard
            subscription={subscription}
            page={page}
            details={details}
            order={order}
          />
          {subscription != null ? (
            <Stack gap={2} direction={isMobile ? "column" : "row"}>
              <ManageSubscriptionCard
                subscription={subscription}
                page={page}
                navigate={navigate}
              />
              <MedRemindersCard subscription={subscription} page={page} />
            </Stack>
          ) : null}
          <MessageCard productSlug={order.product.slug} page={page} />
          <TreatmentPlanCard
            page={page}
            details={details}
            isGlp1={isGlp1}
            numberOfMonths={numberOfMonths}
            category={category}
          />
          {isGlp1 ? <InjectionGuideCard /> : null}
          <RecommendedDose />
        </>
      )}
    </Stack>
  );
};

export default TreatmentOverviewSection;
